import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useMutation } from '@apollo/client';
import { Frames as CheckoutComFrames } from '@monofront/frames-react';

import {
  ICreatePaymentIntentForNewCardQueryResult,
  PaymentMethodSource,
} from '@monofront/types';
import { CREATE_PAYMENT_INTENT_FOR_NEW_CARD } from '@monofront/business/queries';
import {
  useCheckoutCartState,
  useCheckoutComMachine,
} from '@monofront/business/contexts';
import {
  CHECKOUTCOM_CARD_FORM_TRANSITIONS,
  CHECKOUT_COM_CARD_FORM_STATES,
  ICheckoutComContext,
} from '@monofront/business/stateMachines';
import Button from '@monofront/design-system/components/Button';

import { useAuthentication } from '@context/businessContainer';
import { useSubscribeInCheckout } from '@monofront/business/hooks/checkout/useSubscribeInCheckout';
import logger from '@utils/logger';
import useAddPaymentMethod from '@features/PaymentMethods/useAddPaymentMethod';
import { useCheckoutPayment } from '../contexts';
import useCompleteOrder from '../hooks/useCompleteOrder';
import useHandlePaymentError from '../hooks/useHandlePaymentErrors';

/**
 * Display a Pay CTA to pay with a new checkoutCom card
 * On click: create paymentIntent, open 3DS modal, wait for success, attach
 */
const PayWithNewCardCta: React.FC = () => {
  const tCheckout = useTranslations('Checkout');
  const { authUser } = useAuthentication();
  const { checkoutCart } = useCheckoutCartState();
  const {
    isOrderValidationInProgress,
    setIsOrderValidationInProgress,
    selectedNewCheckoutComCard,
  } = useCheckoutPayment();
  const { send, state, registerCompleteOrder } = useCheckoutComMachine();
  const { handleAddNewCard } = useAddPaymentMethod(false, 'checkout');
  const { completeOrder } = useCompleteOrder();
  const { isSubscriptionAvailable } = useSubscribeInCheckout(useAuthentication);
  const { handlePaymentError } = useHandlePaymentError();

  const shouldSaveCard =
    isSubscriptionAvailable ||
    selectedNewCheckoutComCard?.shouldSaveCardForFutureUse;

  const [createPaymentIntentForNewCard] =
    useMutation<ICreatePaymentIntentForNewCardQueryResult>(
      CREATE_PAYMENT_INTENT_FOR_NEW_CARD,
    );

  const onCardValidation = async (context: ICheckoutComContext) => {
    if (shouldSaveCard) {
      try {
        await handleAddNewCard(
          context.paymentId!,
          PaymentMethodSource.CHECKOUT,
        );
      } catch (error: any) {
        handlePaymentError({
          code: 'ATTACH_WITH_NEW_CARD_FAILED',
          message:
            !error?.graphQLErrors && error?.message
              ? error.message
              : 'Cannot attach a new card.',
          error,
          context: {
            selected_card_status: selectedNewCheckoutComCard,
          },
        });
      }
    }

    return completeOrder();
  };

  useEffect(() => {
    registerCompleteOrder(onCardValidation);
  }, [registerCompleteOrder, onCardValidation]);

  const validateOrder = async () => {
    setIsOrderValidationInProgress(true);

    try {
      if (
        !checkoutCart?.id ||
        !checkoutCart?.payment?.id ||
        !selectedNewCheckoutComCard?.paymentMethodCode
      ) {
        throw new Error(
          'Try to pay with a new card with corrumpted checkoutCart.',
        );
      }

      const card = await CheckoutComFrames.submitCard();

      if (!card.token) {
        throw new Error('No token generated for the user new card.');
      }

      send({ type: CHECKOUTCOM_CARD_FORM_TRANSITIONS.SUBMIT_CARD });

      const { data } = await createPaymentIntentForNewCard({
        variables: {
          tokenValue: checkoutCart.id,
          cartPaymentId: checkoutCart.payment.id,
          temporaryPaymentMethodToken: card.token,
          paymentMethodCode: selectedNewCheckoutComCard.paymentMethodCode,
          saveCardForFutureUse:
            isSubscriptionAvailable ||
            selectedNewCheckoutComCard.shouldSaveCardForFutureUse,
        },
      });

      if (!data?.createPaymentIntentForNewCard?.payment) {
        throw new Error('Create Payment Intent failed with empty response.');
      }

      const url3DSecure =
        data.createPaymentIntentForNewCard.payment.financialPaymentRedirectLink;

      if (url3DSecure) {
        send({
          type: CHECKOUTCOM_CARD_FORM_TRANSITIONS.CREATE_PAYMENT_WITH_3DS,
          url3DSecure,
          paymentId:
            data.createPaymentIntentForNewCard.payment.financialPaymentIntentId,
        });
      } else {
        // LF-3153 - keep a log to see if we really do have non-3DS payments and how frequently - will be remove after a few days.
        const warnMessage = {
          message:
            '[checkout] A non 3DS payment has been validated (new card).',
          context: {
            user_id: authUser?.uid,
            cart_id: checkoutCart.id,
            cart_payment_id: checkoutCart.payment.id,
            selected_card_status: selectedNewCheckoutComCard,
          },
        };

        console.warn(warnMessage.message, warnMessage.context);
        logger.warn(warnMessage.message, warnMessage.context);

        // No 3DS means the machine never reaches COMPLETING_ORDER, so this path
        // submits directly and owns its own failure handling.
        if (!(await onCardValidation(state.context))) {
          send({ type: CHECKOUTCOM_CARD_FORM_TRANSITIONS.RESET });
        }
      }
    } catch (error: any) {
      send({ type: CHECKOUTCOM_CARD_FORM_TRANSITIONS.RESET });
      setIsOrderValidationInProgress(false);

      handlePaymentError({
        code: 'PAY_WITH_NEW_CARD_FAILED',
        message:
          !error?.graphQLErrors && error?.message
            ? error.message
            : 'Cannot create a PaymentIntent for a new card or complete an order for a non-3DS card.',
        shouldDisplayError: true,
        error,
        context: {
          selected_card_status: selectedNewCheckoutComCard,
        },
      });
    }
  };

  useEffect(() => {
    // handle checkoutcomCardFormMachine transitions
    if (state.value === CHECKOUT_COM_CARD_FORM_STATES['INITIAL']) {
      setIsOrderValidationInProgress(false);

      return;
    }

    if (state.value === CHECKOUT_COM_CARD_FORM_STATES['3D_SECURE_SUCCEEDED']) {
      // Only ask the machine to submit: it invokes the registered routine once
      // and ignores this event in every later state, so a re-run of this effect
      // cannot complete the order twice.
      send({ type: CHECKOUTCOM_CARD_FORM_TRANSITIONS.COMPLETE_ORDER });
    }
  }, [state]);

  if (!checkoutCart?.id) return null;

  return (
    <Button
      onClick={() => validateOrder()}
      isLoading={isOrderValidationInProgress}
      dataTestId="ActionButtons-order-payment"
    >
      {tCheckout('finishCheckout')}
    </Button>
  );
};

export default PayWithNewCardCta;
